import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { useTranslation } from 'react-i18next';
import { 
  AlertTriangle, Users, FileText, CheckCircle, MapPin, Calendar, 
  Phone, DollarSign, Shield, PhoneCall, MessageSquare, 
  CreditCard, Search, ArrowRight, Eye, TrendingUp, Lock, Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { FraudCard } from '@/components/fraud/FraudCard';
import { PhoneRiskIndicator } from '@/components/fraud/PhoneRiskIndicator';
import { TimelineCardSkeleton, RiskIndicatorSkeleton } from '@/components/ui/skeleton';
import { SearchPagination } from '@/components/ui/search-pagination';
import { useToast } from '@/hooks/use-toast';
import { reportsApi, statsApi, FraudReport, PhoneActivityTimeline } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { format } from 'date-fns';

export function HomePage() {
  const [location] = useLocation();
  const { t } = useTranslation();
  const { toast } = useToast();
  const [searchResults, setSearchResults] = useState<FraudReport[]>([]);
  const [phoneData, setPhoneData] = useState<PhoneActivityTimeline | null>(null);
  const [selectedReport, setSelectedReport] = useState<FraudReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 5;
  const [stats, setStats] = useState({
    totalReports: 0,
    highRiskNumbers: 0,
    totalUsers: 0,
    preventedFrauds: 0
  });

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.split('?')[1] || '');
    const query = urlParams.get('q');
    if (query) {
      setSearchQuery(query);
      handleSearch(query);
    }
  }, [location]);

  const loadStats = async () => {
    const result = await statsApi.getPublic();
    if (result.success && result.data) {
      setStats({
        totalReports: result.data.total_reports,
        highRiskNumbers: result.data.high_risk_numbers,
        totalUsers: result.data.total_users,
        preventedFrauds: result.data.prevented_frauds
      });
    }
  };

  // Sanitize and validate Bangladesh phone numbers
  // Normalizes to +880XXXXXXXXX format to match backend storage
  const sanitizePhoneNumber = (input: string): { isValid: boolean; sanitized: string; error?: string } => {
    // Remove all non-digit characters
    const cleaned = input.trim().replace(/[^\d]/g, '');
    
    if (!cleaned) {
      return { isValid: false, sanitized: '', error: 'Please enter a phone number.' };
    }
    
    // Bangladesh formats:
    // Local: 01XXXXXXXXX (11 digits) - mobile starts with 01[3-9]
    // International: 8801XXXXXXXXX (12 digits) - country code 880 + 1XXXXXXXXX
    // VoIP local: 096XXXXXXXX (11 digits)
    // VoIP international: 88096XXXXXXXX (13 digits)
    
    // Mobile: 01[3-9]XXXXXXXX (11 digits local) or 880[1][3-9]XXXXXXXX (12 digits intl)
    const mobileLocalRegex = /^(01[3-9]\d{8})$/;  // 01777777777
    const mobileIntlRegex = /^880(1[3-9]\d{8})$/;  // 8801777777777
    
    // VoIP: 096XXXXXXXX (11 digits local) or 88096XXXXXXXX (13 digits intl)
    const voipLocalRegex = /^(096\d{8})$/;  // 09612345678
    const voipIntlRegex = /^880(96\d{8})$/;  // 880961234567
    
    // Check mobile local format (01XXXXXXXXX)
    const mobileLocalMatch = cleaned.match(mobileLocalRegex);
    if (mobileLocalMatch) {
      // 01777777777 -> +8801777777777
      return { isValid: true, sanitized: '+880' + mobileLocalMatch[1].substring(1) };
    }
    
    // Check mobile international format (8801XXXXXXXXX)
    const mobileIntlMatch = cleaned.match(mobileIntlRegex);
    if (mobileIntlMatch) {
      // 8801777777777 -> +8801777777777
      return { isValid: true, sanitized: '+880' + mobileIntlMatch[1] };
    }
    
    // Check VoIP local format (096XXXXXXXX)
    const voipLocalMatch = cleaned.match(voipLocalRegex);
    if (voipLocalMatch) {
      // 09612345678 -> +88096123456
      return { isValid: true, sanitized: '+880' + voipLocalMatch[1].substring(1) };
    }
    
    // Check VoIP international format (88096XXXXXXXX)
    const voipIntlMatch = cleaned.match(voipIntlRegex);
    if (voipIntlMatch) {
      // 88096123456 -> +88096123456
      return { isValid: true, sanitized: '+880' + voipIntlMatch[1] };
    }
    
    // Provide specific error messages
    if (cleaned.length < 11) {
      return { isValid: false, sanitized: '', error: 'Phone number is too short. Bangladesh numbers have 11 digits (e.g., 01712345678).' };
    }
    if (cleaned.length > 13) {
      return { isValid: false, sanitized: '', error: 'Phone number is too long. Please enter a valid Bangladesh number.' };
    }
    
    // Check if it starts with valid prefix
    if (!cleaned.match(/^(?:880)?0?1[3-9]/) && !cleaned.match(/^(?:880)?0?96/)) {
      return { 
        isValid: false, 
        sanitized: '', 
        error: 'Invalid prefix. Mobile numbers start with 013-019, VoIP numbers start with 096.' 
      };
    }
    
    return { 
      isValid: false, 
      sanitized: '', 
      error: 'Invalid Bangladesh phone number format.' 
    };
  };

  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      toast({
        title: "Invalid Search",
        description: "Please enter a phone number to search.",
        variant: "destructive",
      });
      return;
    }
    
    // Sanitize and validate the phone number
    const { isValid, sanitized, error } = sanitizePhoneNumber(query);
    if (!isValid) {
      toast({
        title: "Invalid Phone Number",
        description: error || "Please enter a valid Bangladesh phone number.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    setSearchQuery(sanitized);
    setHasSearched(true);
    setCurrentPage(1);
    
    try {
      const result = await reportsApi.search(sanitized);
      if (!result.success) {
        toast({
          title: "Search Error",
          description: result.error || "Failed to search reports.",
          variant: "destructive",
        });
        return;
      }
      setSearchResults(result.data?.reports || []);
      setPhoneData(result.data?.phone_activity || null);
    } catch (error) {
      toast({
        title: "Search Error",
        description: "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch(searchQuery);
  };

  const handleViewDetails = (report: FraudReport) => {
    setSelectedReport(report);
  };

  const fraudTypes = [
    { icon: PhoneCall, label: t('fraudTypes.phoneScams'), desc: t('fraudTypes.phoneScamsDesc'), gradient: 'from-red-500 to-rose-600' },
    { icon: MessageSquare, label: t('fraudTypes.smsFraud'), desc: t('fraudTypes.smsFraudDesc'), gradient: 'from-orange-500 to-amber-600' },
    { icon: CreditCard, label: t('fraudTypes.financialFraud'), desc: t('fraudTypes.financialFraudDesc'), gradient: 'from-violet-500 to-purple-600' },
    { icon: Users, label: t('fraudTypes.romanceScams'), desc: t('fraudTypes.romanceScamsDesc'), gradient: 'from-pink-500 to-rose-600' },
  ];

  return (
    <div className="min-h-screen relative">
      {/* Full Page Animated Background */}
      <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 -z-10">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-0 -left-40 w-80 h-80 bg-green-500 rounded-full mix-blend-multiply filter blur-3xl animate-pulse"></div>
          <div className="absolute top-0 -right-40 w-80 h-80 bg-emerald-500 rounded-full mix-blend-multiply filter blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute -bottom-40 left-1/2 w-80 h-80 bg-teal-500 rounded-full mix-blend-multiply filter blur-3xl animate-pulse delay-500"></div>
        </div>
        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.02)_1px,transparent_1px)] bg-[size:50px_50px]"></div>
      </div>

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">

        <div className="relative w-full px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-5xl mx-auto">
            {/* Top Badge */}
            <div className="flex justify-center mb-8">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 text-green-400 px-5 py-2.5 rounded-full text-sm font-medium backdrop-blur-sm">
                <Sparkles className="h-4 w-4" />
                {t('home.trustedBy', { count: stats.totalUsers })}
              </div>
            </div>

            {/* Main Heading */}
            <h1 className="text-center text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white mb-6 tracking-tight">
              <span className="block">{t('home.heroTitle1')}</span>
              <span className="block bg-gradient-to-r from-green-400 via-emerald-400 to-teal-400 bg-clip-text text-transparent">
                {t('home.heroTitle2')}
              </span>
            </h1>

            <p className="text-center text-lg sm:text-xl text-slate-400 mb-12 max-w-2xl mx-auto leading-relaxed">
              {t('home.heroDescription')}
              <span className="text-white font-semibold"> {stats.totalReports.toLocaleString()}+ </span>
              {t('home.reportedScams')}
            </p>

            {/* Search Box */}
            <form onSubmit={handleSubmit} className="max-w-2xl mx-auto mb-12">
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl blur opacity-25 group-hover:opacity-40 transition duration-500"></div>
                <div className="relative flex flex-col sm:flex-row gap-3 bg-slate-800/80 backdrop-blur-xl p-3 rounded-2xl border border-slate-700/50">
                  <div className="flex-1 relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
                    <Input
                      type="text"
                      placeholder={t('home.searchPlaceholder')}
                      value={searchQuery}
                      onChange={(e) => {
                        // Only allow digits and + at the start
                        const value = e.target.value;
                        const sanitized = value.startsWith('+') 
                          ? '+' + value.slice(1).replace(/\D/g, '')
                          : value.replace(/\D/g, '');
                        setSearchQuery(sanitized);
                      }}
                      inputMode="tel"
                      maxLength={14}
                      className="pl-12 h-14 text-lg border-0 bg-slate-900/50 text-white placeholder:text-slate-500 focus-visible:ring-1 focus-visible:ring-green-500 rounded-xl"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    size="lg" 
                    className="h-14 px-8 text-base font-semibold bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 rounded-xl shadow-lg shadow-green-500/25"
                    disabled={loading}
                  >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                        {t('home.checking')}
                      </div>
                    ) : (
                      <>
                        <Search className="mr-2 h-5 w-5" />
                        {t('home.checkNumber')}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </form>

            {/* Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
              {[
                { value: stats.totalReports, label: t('home.totalReports'), icon: FileText },
                { value: stats.highRiskNumbers, label: t('home.highRisk'), icon: AlertTriangle },
                { value: stats.totalUsers, label: t('home.members'), icon: Users },
                { value: stats.preventedFrauds, label: t('home.prevented'), icon: Shield },
              ].map((stat, i) => (
                <div key={i} className="bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4 text-center">
                  <stat.icon className="h-5 w-5 text-green-400 mx-auto mb-2" />
                  <div className="text-2xl sm:text-3xl font-bold text-white">{stat.value.toLocaleString()}</div>
                  <div className="text-sm text-slate-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        {!hasSearched && (
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <div className="w-6 h-10 border-2 border-slate-600 rounded-full flex justify-center pt-2">
              <div className="w-1.5 h-3 bg-green-400 rounded-full animate-pulse"></div>
            </div>
          </div>
        )}
      </section>

      {/* Search Results Section */}
      {hasSearched && (
        <section className="py-16 relative">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {loading ? (
              <div className="space-y-8">
                {/* Skeleton Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-slate-700">
                  <div>
                    <div className="h-4 w-24 bg-slate-700 rounded animate-pulse mb-2" />
                    <div className="h-8 w-48 bg-slate-700 rounded animate-pulse" />
                  </div>
                  <div className="h-10 w-32 bg-slate-700 rounded animate-pulse" />
                </div>
                {/* Risk Indicator Skeleton */}
                <RiskIndicatorSkeleton />
                {/* Report Skeletons */}
                <div className="grid lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2 space-y-4">
                    <div className="h-6 w-32 bg-slate-700 rounded animate-pulse" />
                    <TimelineCardSkeleton />
                    <TimelineCardSkeleton />
                    <TimelineCardSkeleton />
                  </div>
                  <div className="space-y-4">
                    <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4 space-y-3">
                      <div className="h-6 w-24 bg-slate-700 rounded animate-pulse" />
                      {[...Array(4)].map((_, i) => (
                        <div key={i} className="h-4 w-full bg-slate-700 rounded animate-pulse" />
                      ))}
                    </div>
                    <div className="h-12 w-full bg-slate-700 rounded animate-pulse" />
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-8">
                {/* Result Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-slate-700">
                  <div>
                    <p className="text-sm text-slate-400 mb-1">{t('home.searchResults')}</p>
                    <h2 className="text-2xl sm:text-3xl font-bold text-white font-mono">{searchQuery}</h2>
                  </div>
                  <Button 
                    variant="outline" 
                    onClick={() => { setHasSearched(false); setSearchQuery(''); }}
                    className="border-slate-600 bg-slate-800 text-slate-300 hover:text-white hover:bg-slate-700"
                  >
                    <Search className="h-4 w-4 mr-2" />
                    {t('home.newSearch')}
                  </Button>
                </div>

                {/* Risk Indicator */}
                {phoneData && (
                  <PhoneRiskIndicator phoneData={phoneData} phoneNumber={searchQuery} />
                )}

                {/* Warning Card - Fraud Found */}
                {!phoneData && searchResults.length > 0 && (
                  <div className="relative overflow-hidden bg-gradient-to-br from-red-50 to-rose-50 border-2 border-red-200 rounded-2xl p-6 sm:p-8">
                    <div className="absolute top-0 right-0 w-40 h-40 bg-red-100 rounded-full -translate-y-1/2 translate-x-1/2 opacity-50"></div>
                    <div className="relative flex flex-col sm:flex-row items-start gap-5">
                      <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-red-500/30">
                        <AlertTriangle className="h-8 w-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-red-800 mb-2">
                          {t('home.dangerFound')}
                        </h3>
                        <p className="text-red-700 mb-4 text-lg">
                          {t('home.dangerDescription', { count: searchResults.length })}
                          <span className="block mt-1 text-base">{t('home.dangerWarning')}</span>
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <Badge className="bg-red-600 hover:bg-red-700 text-sm px-3 py-1">{t('risk.high')}</Badge>
                          <Badge variant="outline" className="border-red-400 text-red-700 text-sm px-3 py-1">
                            {searchResults.length} {t('home.fraudReports')}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Safe Card - No Reports */}
                {!phoneData && searchResults.length === 0 && (
                  <div className="relative overflow-hidden bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-2xl p-6 sm:p-8">
                    <div className="absolute top-0 right-0 w-40 h-40 bg-green-100 rounded-full -translate-y-1/2 translate-x-1/2 opacity-50"></div>
                    <div className="relative flex flex-col sm:flex-row items-start gap-5">
                      <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-green-500/30">
                        <CheckCircle className="h-8 w-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-green-800 mb-2">
                          {t('home.noReportsFound')}
                        </h3>
                        <p className="text-green-700 mb-4 text-lg">
                          {t('home.noReportsDescription')}
                          <span className="block mt-1 text-base">{t('home.noReportsWarning')}</span>
                        </p>
                        <Button 
                          onClick={() => window.location.href = '/report'}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <AlertTriangle className="h-4 w-4 mr-2" />
                          {t('home.reportIfSuspicious')}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Reports Grid */}
                {searchResults.length > 0 && (
                  <div className="grid lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2 space-y-4">
                      <h3 className="font-bold text-white text-lg flex items-center gap-2">
                        <Eye className="h-5 w-5 text-slate-400" />
                        {searchResults.length} Fraud Report{searchResults.length > 1 ? 's' : ''}
                      </h3>
                      {searchResults
                        .slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)
                        .map((report) => (
                          <FraudCard
                            key={report.id}
                            report={report}
                            onViewDetails={handleViewDetails}
                            showPhoneNumber={false}
                          />
                        ))}
                      {searchResults.length > ITEMS_PER_PAGE && (
                        <SearchPagination
                          currentPage={currentPage}
                          totalPages={Math.ceil(searchResults.length / ITEMS_PER_PAGE)}
                          totalItems={searchResults.length}
                          itemsPerPage={ITEMS_PER_PAGE}
                          onPageChange={setCurrentPage}
                          className="pt-4"
                        />
                      )}
                    </div>
                    
                    {/* Sidebar */}
                    <div className="space-y-4">
                      <Card className="border-slate-700 shadow-sm bg-slate-800/50 backdrop-blur-sm">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-lg flex items-center gap-2 text-white">
                            <Shield className="h-5 w-5 text-green-500" />
                            {t('home.safetyTips')}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3 text-sm">
                          <p className="flex items-start gap-2 text-green-400">
                            <span className="text-green-500 mt-0.5">✓</span>
                            {t('home.tip1')}
                          </p>
                          <p className="flex items-start gap-2 text-green-400">
                            <span className="text-green-500 mt-0.5">✓</span>
                            {t('home.tip2')}
                          </p>
                          <p className="flex items-start gap-2 text-green-400">
                            <span className="text-green-500 mt-0.5">✓</span>
                            {t('home.tip3')}
                          </p>
                          <p className="flex items-start gap-2 text-green-400">
                            <span className="text-green-500 mt-0.5">✓</span>
                            {t('home.tip4')}
                          </p>
                        </CardContent>
                      </Card>
                      
                      <Button 
                        className="w-full bg-green-600 hover:bg-green-700" 
                        size="lg" 
                        onClick={() => window.location.href = '/report'}
                      >
                        <AlertTriangle className="h-4 w-4 mr-2" />
                        {t('home.reportThisNumber')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Features Section - Only show when not searching */}
      {!hasSearched && (
        <>
          {/* Fraud Types Section */}
          <section className="py-20 relative">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-14">
                <Badge className="bg-green-500/20 text-green-400 hover:bg-green-500/20 border-green-500/30 mb-4">{t('fraudTypes.title')}</Badge>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  {t('fraudTypes.title')}
                </h2>
                <p className="text-slate-400 text-lg max-w-2xl mx-auto">
                  {t('fraudTypes.description')}
                </p>
              </div>
              
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {fraudTypes.map((type, index) => (
                  <div 
                    key={index} 
                    className="group relative bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-green-500/50 hover:shadow-xl hover:shadow-green-500/10 transition-all duration-300 cursor-pointer"
                  >
                    <div className={`w-14 h-14 bg-gradient-to-br ${type.gradient} rounded-xl flex items-center justify-center mb-5 text-white shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <type.icon className="h-7 w-7" />
                    </div>
                    <h3 className="font-bold text-white text-lg mb-2">{type.label}</h3>
                    <p className="text-slate-400 text-sm">{type.desc}</p>
                    <ArrowRight className="absolute bottom-6 right-6 h-5 w-5 text-slate-600 group-hover:text-green-400 group-hover:translate-x-1 transition-all" />
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* How It Works */}
          <section className="py-20 relative">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-14">
                <Badge className="bg-green-500/20 text-green-400 hover:bg-green-500/20 border-green-500/30 mb-4">{t('howItWorks.title')}</Badge>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  {t('howItWorks.title')}
                </h2>
                <p className="text-slate-400 text-lg">
                  {t('howItWorks.description')}
                </p>
              </div>
              
              <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
                {[
                  { 
                    num: '01', 
                    title: t('howItWorks.step1Title'), 
                    desc: t('howItWorks.step1Desc'),
                    icon: Search
                  },
                  { 
                    num: '02', 
                    title: t('howItWorks.step2Title'), 
                    desc: t('howItWorks.step2Desc'),
                    icon: Eye
                  },
                  { 
                    num: '03', 
                    title: t('howItWorks.step3Title'), 
                    desc: t('howItWorks.step3Desc'),
                    icon: FileText
                  },
                ].map((step, index) => (
                  <div key={index} className="relative text-center">
                    <div className="relative inline-block mb-6">
                      <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center text-white shadow-xl shadow-green-500/20 rotate-3 group-hover:rotate-0 transition-transform">
                        <step.icon className="h-9 w-9" />
                      </div>
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-slate-900 rounded-lg flex items-center justify-center text-white text-sm font-bold">
                        {step.num}
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">{step.title}</h3>
                    <p className="text-slate-400 leading-relaxed">{step.desc}</p>
                    
                    {index < 2 && (
                      <div className="hidden md:block absolute top-10 left-[65%] w-[70%] border-t-2 border-dashed border-slate-600"></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Stats Section */}
          <section className="py-20 relative overflow-hidden">
            
            <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-14">
                <Badge className="bg-green-500/20 text-green-400 hover:bg-green-500/20 border-green-500/30 mb-4">
                  <TrendingUp className="h-3 w-3 mr-1" />
                  {t('stats.title')}
                </Badge>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  {t('stats.title')}
                </h2>
                <p className="text-slate-400 text-lg max-w-2xl mx-auto">
                  {t('stats.description')}
                </p>
              </div>
              
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { value: stats.totalReports, label: t('stats.fraudReports'), icon: FileText, color: 'from-red-500 to-rose-600' },
                  { value: stats.highRiskNumbers, label: t('stats.highRiskNumbers'), icon: AlertTriangle, color: 'from-orange-500 to-amber-600' },
                  { value: stats.totalUsers, label: t('stats.communityMembers'), icon: Users, color: 'from-blue-500 to-indigo-600' },
                  { value: stats.preventedFrauds, label: t('stats.fraudsPrevented'), icon: Shield, color: 'from-green-500 to-emerald-600' },
                ].map((stat, i) => (
                  <div key={i} className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 text-center hover:bg-slate-800/70 transition-colors">
                    <div className={`w-12 h-12 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                      <stat.icon className="h-6 w-6 text-white" />
                    </div>
                    <div className="text-3xl sm:text-4xl font-bold text-white mb-1">{stat.value.toLocaleString()}</div>
                    <div className="text-slate-400 text-sm">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 relative">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="relative bg-gradient-to-br from-green-600 to-emerald-700 rounded-3xl p-8 sm:p-12 overflow-hidden">
                {/* Background decoration */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2"></div>
                
                <div className="relative text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-2xl mb-6">
                    <Lock className="h-8 w-8 text-white" />
                  </div>
                  <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                    {t('cta.title')}
                  </h2>
                  <p className="text-green-100 text-lg mb-8 max-w-xl mx-auto">
                    {t('cta.description')}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button 
                      size="lg" 
                      className="bg-white text-green-700 hover:bg-green-50 text-base font-semibold h-14 px-8 rounded-xl"
                      onClick={() => window.location.href = '/register'}
                    >
                      <Users className="h-5 w-5 mr-2" />
                      {t('cta.createAccount')}
                    </Button>
                    <Button 
                      size="lg" 
                      variant="outline" 
                      className="border-2 border-white/30 text-white hover:bg-white/10 text-base font-semibold h-14 px-8 rounded-xl"
                      onClick={() => window.location.href = '/report'}
                    >
                      <FileText className="h-5 w-5 mr-2" />
                      {t('cta.reportFraud')}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      )}

      {/* Report Details Dialog */}
      <Dialog open={!!selectedReport} onOpenChange={(open) => !open && setSelectedReport(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-800 border-slate-700">
          {selectedReport && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-xl text-white">
                  <div className="w-10 h-10 bg-red-500/20 rounded-xl flex items-center justify-center">
                    <AlertTriangle className="h-5 w-5 text-red-400" />
                  </div>
                  {selectedReport.title}
                </DialogTitle>
                <DialogDescription className="text-slate-400">
                  Detailed fraud report information
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6 mt-4">
                <div className="flex flex-wrap gap-2">
                  <Badge className={selectedReport.status === 'approved' ? 'bg-green-500/20 text-green-400 border-green-500/30' : selectedReport.status === 'rejected' ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'}>
                    {selectedReport.status.charAt(0).toUpperCase() + selectedReport.status.slice(1)}
                  </Badge>
                  <Badge variant="outline" className="border-slate-600 text-slate-300">
                    {selectedReport.fraud_category.charAt(0).toUpperCase() + selectedReport.fraud_category.slice(1)}
                  </Badge>
                  <Badge className={selectedReport.urgency_level === 'high' ? 'bg-red-500/20 text-red-400' : selectedReport.urgency_level === 'medium' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-slate-700 text-slate-300'}>
                    {selectedReport.urgency_level.charAt(0).toUpperCase() + selectedReport.urgency_level.slice(1)} Urgency
                  </Badge>
                </div>

                <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-3 text-red-400">
                    <Phone className="h-5 w-5" />
                    <span className="font-mono text-xl font-bold">{selectedReport.phone_number}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2 text-slate-300 bg-slate-700/50 rounded-lg p-3">
                    <MapPin className="h-4 w-4 text-slate-400" />
                    <span>{selectedReport.district}, {selectedReport.division}</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-300 bg-slate-700/50 rounded-lg p-3">
                    <Calendar className="h-4 w-4 text-slate-400" />
                    <span>{format(new Date(selectedReport.created_at), 'PPP')}</span>
                  </div>
                  {selectedReport.amount_involved && (
                    <div className="flex items-center gap-2 text-slate-300 bg-slate-700/50 rounded-lg p-3">
                      <DollarSign className="h-4 w-4 text-slate-400" />
                      <span>৳{selectedReport.amount_involved.toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-slate-300 bg-slate-700/50 rounded-lg p-3">
                    <FileText className="h-4 w-4 text-slate-400" />
                    <span>Via {selectedReport.medium}</span>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-white mb-3">Description</h4>
                  <p className="text-slate-300 whitespace-pre-wrap bg-slate-700/50 p-4 rounded-xl text-sm leading-relaxed border border-slate-600">
                    {selectedReport.description}
                  </p>
                </div>

                {selectedReport.transaction_method && (
                  <div>
                    <h4 className="font-semibold text-white mb-2">Transaction Method</h4>
                    <Badge variant="outline" className="text-sm border-slate-600 text-slate-300">{selectedReport.transaction_method}</Badge>
                  </div>
                )}

                <div className="flex gap-3 pt-4 border-t border-slate-700">
                  <Button variant="outline" className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700" onClick={() => setSelectedReport(null)}>
                    Close
                  </Button>
                  <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={() => window.location.href = '/report'}>
                    Report Similar Fraud
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
